import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { RetroWindow } from "../components/RetroWindow";
import { SectionHeading } from "../components/SectionHeading";
import { SiteFavicon } from "../components/SiteFavicon";
import { fetchProjects, type PublicProject } from "../lib/api";

export function Work() {
  const [projects, setProjects] = useState<PublicProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchProjects()
      .then((rows) => {
        if (!cancelled) setProjects(rows);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const item = projects && projects[active];

  return (
    <section id="work" className="relative py-24 md:py-32 px-4 md:px-8">
      <div className="mx-auto max-w-[1400px]">
        <SectionHeading
          kicker="// WORK"
          title={
            <>
              Real work.{" "}
              <span className="shimmer-text">Embedded live, not screenshotted.</span>
            </>
          }
          blurb="Each one below is a deployed site we built. Click into the window — you can scroll and interact with it like the real thing. Because it is."
        />

        {error && (
          <div className="bevel-in bg-void p-4 font-terminal text-ember">
            ⚠ Couldn't load projects: {error}
          </div>
        )}

        {!error && projects === null && (
          <div className="bevel-in bg-void p-6 font-terminal text-mystic">
            loading…
          </div>
        )}

        {projects && projects.length === 0 && (
          <div className="bevel-in bg-night p-6 text-center font-terminal text-aura/70">
            No work to show yet — check back soon.
          </div>
        )}

        {/* Mobile: list of project cards with links — embedding live sites in
            iframes is janky on touch and the curve effect doesn't translate to
            small viewports anyway. Desktop keeps the CRT tube treatment. */}
        {projects && projects.length > 0 && (
          <ul className="md:hidden space-y-4">
            {projects.map((p) => (
              <li key={p.id} className="bevel-out bg-grimoire p-1">
                <div className="bg-night p-5 border-2 border-black">
                  <div className="flex items-center gap-3">
                    <SiteFavicon
                      url={p.url}
                      size={40}
                      className="bevel-in p-1 bg-void shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-terminal text-mystic text-xs uppercase tracking-widest">
                        Project
                      </div>
                      <h3 className="font-display text-base text-bone leading-tight">
                        {p.client}
                      </h3>
                    </div>
                  </div>
                  {p.notes && (
                    <p className="mt-3 font-body text-sm text-aura/85 leading-relaxed">
                      {p.notes}
                    </p>
                  )}
                  {p.stack.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-1.5">
                      {p.stack.map((s) => (
                        <li
                          key={s}
                          className="font-terminal text-xs text-bone bg-bruise px-2 py-0.5 border border-witch"
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 block text-center w-full font-display text-[10px] uppercase tracking-widest px-3 py-3 bg-acid text-void border-2 border-black shadow-[3px_3px_0_0_#000] active:translate-y-[2px] active:translate-x-[2px] active:shadow-[1px_1px_0_0_#000] transition-all pointer"
                  >
                    Visit live site ↗
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}

        {item && (
          <div className="hidden md:block">
            {projects.length > 1 && (
              <ul className="flex flex-wrap gap-2 mb-6">
                {projects.map((p, i) => {
                  const on = i === active;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => setActive(i)}
                        className={`font-display text-[10px] uppercase tracking-wider px-3 py-2 border-2 transition-all pointer
                          ${
                            on
                              ? "bg-witch text-bone border-black shadow-[3px_3px_0_0_#000]"
                              : "bg-grimoire/60 text-aura border-bruise hover:border-mystic"
                          }
                        `}
                      >
                        {p.title.split("—")[0]}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
              <RetroWindow
                title={item.title}
                url={item.url}
                status={`Loaded · scroll & click inside the tube`}
                motionProps={{
                  initial: { opacity: 0, y: 24 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true, margin: "-80px" },
                  transition: { duration: 0.5 },
                }}
                className="overflow-hidden"
                bodyClassName="relative"
              >
                <div className="crt-bezel relative aspect-[16/10] w-full">
                  <div
                    className="crt-tube absolute inset-3 md:inset-5"
                    style={{ clipPath: "url(#crt-barrel)" }}
                  >
                    <iframe
                      key={item.id}
                      src={item.iframeSrc}
                      title={item.title}
                      className="absolute bg-bone"
                      style={{
                        top: "-3%",
                        left: "-3%",
                        width: "106%",
                        height: "106%",
                      }}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                    <div className="crt-scanlines" />
                    <div className="crt-tracking" />
                  </div>

                  <span
                    className="crt-led"
                    style={{ bottom: 10, right: 14 }}
                    aria-hidden
                  />

                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 left-3 md:bottom-3 md:left-5 font-display text-[9px] uppercase tracking-widest px-2 py-1 bg-witch text-bone border-2 border-black shadow-[3px_3px_0_0_#000] hover:bg-mystic hover:text-void hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[1px_1px_0_0_#000] transition-all z-10"
                  >
                    Open in new tab ↗
                  </a>
                </div>
              </RetroWindow>

              <motion.aside
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="bevel-out bg-grimoire p-1"
              >
                <div className="bg-night p-5 border-2 border-black space-y-5">
                  <div className="flex items-center gap-3">
                    <SiteFavicon
                      url={item.url}
                      size={36}
                      className="bevel-in p-1 bg-void shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-terminal text-mystic text-xs uppercase tracking-widest">
                        Project
                      </div>
                      <div className="font-display text-base text-bone leading-tight">
                        {item.client}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="font-terminal text-mystic text-xs uppercase tracking-widest">
                      Stack
                    </div>
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {item.stack.map((t) => (
                        <li
                          key={t}
                          className="font-terminal text-sm text-bone bg-bruise px-2 py-0.5 border border-witch"
                        >
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {item.notes && (
                    <div>
                      <div className="font-terminal text-mystic text-xs uppercase tracking-widest">
                        Notes
                      </div>
                      <p className="mt-2 font-body text-sm text-aura/85 leading-relaxed">
                        {item.notes}
                      </p>
                    </div>
                  )}
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center w-full font-display text-[10px] uppercase tracking-widest px-3 py-2 bg-acid text-void border-2 border-black shadow-[3px_3px_0_0_#000] hover:bg-bone hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[1px_1px_0_0_#000] transition-all pointer"
                  >
                    Visit live site ↗
                  </a>
                </div>
              </motion.aside>
            </div>
          </div>
        )}

        <p className="mt-8 font-terminal text-mystic text-sm">
          // more projects shipping soon — or {" "}
          <a
            href="#contact"
            className="text-acid underline decoration-dotted underline-offset-4 hover:text-bone"
          >
            get in touch and brief your own
          </a>
          .
        </p>
      </div>
    </section>
  );
}
